/**
 * Accounting Tags API Service
 *
 * Provides functions for interacting with the accounting tags API endpoints.
 * All functions use fetch and return typed responses based on the Pact contract.
 *
 * @see docs/plans/iterations/accounting-tags/contracts/frontend-rails-api.json
 */

import type {
  Tag,
  TagType,
  TagWithStats,
  TransactionTag,
  Tagging,
  TaggingRule,
  PendingTaggingRule,
  VendorSpendingReport,
  RecurringExpensesReport,
  ProjectCostsReport,
  TaxDeductionsReport,
  CreateTagRequest,
  UpdateTagRequest,
  ApprovePendingRuleRequest,
  TagsListResponse,
  TagResponse,
  TagMutationResponse,
  TransactionTagsResponse,
  AddTagResponse,
  ApplyRulesResponse,
  TaggingRulesListResponse,
  PendingRulesListResponse,
  ApprovePendingRuleResponse,
  RejectPendingRuleResponse,
  TagsErrorResponse,
} from '@/types/accountingTags';

// =============================================================================
// API Configuration
// =============================================================================

const API_BASE = '/api/accounting';

/**
 * Default fetch options with CSRF token and JSON content type
 */
function getDefaultOptions(): RequestInit {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    credentials: 'same-origin',
  };
}

/**
 * Helper to handle API responses
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new ApiError(
      errorData.error || `Request failed with status ${response.status}`,
      response.status,
      errorData
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: TagsErrorResponse
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// =============================================================================
// Tags API
// =============================================================================

/**
 * Get all tags, optionally filtered by type
 */
export async function getTags(type?: TagType): Promise<Tag[]> {
  const url = type
    ? `${API_BASE}/tags?type=${encodeURIComponent(type)}`
    : `${API_BASE}/tags`;

  const response = await fetch(url, getDefaultOptions());
  const data = await handleResponse<TagsListResponse>(response);
  return data.tags;
}

/**
 * Get a single tag by ID
 */
export async function getTag(id: number): Promise<TagWithStats> {
  const response = await fetch(`${API_BASE}/tags/${id}`, getDefaultOptions());
  const data = await handleResponse<TagResponse>(response);
  return data.tag;
}

/**
 * Create a new tag
 */
export async function createTag(
  tagType: TagType,
  value: string,
  displayName?: string,
  color?: string,
  description?: string
): Promise<Tag> {
  const body: CreateTagRequest = {
    tag: {
      tagType,
      value,
      ...(displayName && { displayName }),
      ...(color && { color }),
      ...(description && { description }),
    },
  };

  const response = await fetch(`${API_BASE}/tags`, {
    ...getDefaultOptions(),
    method: 'POST',
    body: JSON.stringify(body),
  });

  const data = await handleResponse<TagMutationResponse>(response);
  return data.tag;
}

/**
 * Update an existing tag
 */
export async function updateTag(
  id: number,
  updates: {
    displayName?: string;
    color?: string;
    description?: string;
    active?: boolean;
  }
): Promise<Tag> {
  const body: UpdateTagRequest = { tag: updates };

  const response = await fetch(`${API_BASE}/tags/${id}`, {
    ...getDefaultOptions(),
    method: 'PATCH',
    body: JSON.stringify(body),
  });

  const data = await handleResponse<TagMutationResponse>(response);
  return data.tag;
}

/**
 * Delete a tag (soft delete)
 */
export async function deleteTag(id: number): Promise<void> {
  const response = await fetch(`${API_BASE}/tags/${id}`, {
    ...getDefaultOptions(),
    method: 'DELETE',
  });

  await handleResponse<void>(response);
}

// =============================================================================
// Transaction Tags API
// =============================================================================

/**
 * Get tags for a transaction
 */
export async function getTransactionTags(transactionId: number): Promise<TransactionTag[]> {
  const response = await fetch(
    `${API_BASE}/transactions/${transactionId}/tags`,
    getDefaultOptions()
  );
  const data = await handleResponse<TransactionTagsResponse>(response);
  return data.tags;
}

/**
 * Add a tag to a transaction
 * @param transactionId The transaction ID
 * @param tagString Full tag string like "vendor:amazon" or "recurring:monthly"
 */
export async function addTagToTransaction(
  transactionId: number,
  tagString: string
): Promise<Tagging> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/tags`, {
    ...getDefaultOptions(),
    method: 'POST',
    body: JSON.stringify({ tag: tagString }),
  });

  const data = await handleResponse<AddTagResponse>(response);
  return data.tagging;
}

/**
 * Remove a tag from a transaction
 * @param transactionId The transaction ID
 * @param tagString Full tag string like "vendor:amazon"
 */
export async function removeTagFromTransaction(
  transactionId: number,
  tagString: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/transactions/${transactionId}/tags`, {
    ...getDefaultOptions(),
    method: 'DELETE',
    body: JSON.stringify({ tag: tagString }),
  });

  await handleResponse<void>(response);
}

/**
 * Apply tagging rules to a transaction
 */
export async function applyRulesToTransaction(
  transactionId: number
): Promise<ApplyRulesResponse> {
  const response = await fetch(
    `${API_BASE}/transactions/${transactionId}/tags/apply_rules`,
    {
      ...getDefaultOptions(),
      method: 'POST',
    }
  );

  return handleResponse<ApplyRulesResponse>(response);
}

// =============================================================================
// Tagging Rules API
// =============================================================================

/**
 * Get all tagging rules
 */
export async function getTaggingRules(): Promise<TaggingRule[]> {
  const response = await fetch(`${API_BASE}/tagging_rules`, getDefaultOptions());
  const data = await handleResponse<TaggingRulesListResponse>(response);
  return data.taggingRules;
}

/**
 * Create a new tagging rule
 */
export async function createTaggingRule(
  name: string,
  pattern: string,
  tagId: number,
  priority?: number
): Promise<TaggingRule> {
  // Rails expects snake_case keys in the request body
  const body = {
    tagging_rule: {
      name,
      pattern,
      tag_id: tagId,
      ...(priority !== undefined && { priority }),
    },
  };

  const response = await fetch(`${API_BASE}/tagging_rules`, {
    ...getDefaultOptions(),
    method: 'POST',
    body: JSON.stringify(body),
  });

  // Rails returns snake_case keys, so we access tagging_rule directly
  const data = await handleResponse<{ tagging_rule: TaggingRule }>(response);
  return data.tagging_rule;
}

/**
 * Update an existing tagging rule
 */
export async function updateTaggingRule(
  id: number,
  updates: {
    name?: string;
    pattern?: string;
    tagId?: number;
    priority?: number;
    active?: boolean;
  }
): Promise<TaggingRule> {
  // Rails expects snake_case keys in the request body
  const { tagId, ...rest } = updates;
  const body = {
    tagging_rule: {
      ...rest,
      ...(tagId !== undefined && { tag_id: tagId }),
    },
  };

  const response = await fetch(`${API_BASE}/tagging_rules/${id}`, {
    ...getDefaultOptions(),
    method: 'PATCH',
    body: JSON.stringify(body),
  });

  // Rails returns snake_case keys, so we access tagging_rule directly
  const data = await handleResponse<{ tagging_rule: TaggingRule }>(response);
  return data.tagging_rule;
}

/**
 * Delete a tagging rule
 */
export async function deleteTaggingRule(id: number): Promise<void> {
  const response = await fetch(`${API_BASE}/tagging_rules/${id}`, {
    ...getDefaultOptions(),
    method: 'DELETE',
  });

  await handleResponse<void>(response);
}

// =============================================================================
// Pending Tagging Rules API
// =============================================================================

/**
 * Get all pending tagging rules
 */
export async function getPendingTaggingRules(): Promise<PendingTaggingRule[]> {
  const response = await fetch(`${API_BASE}/pending_tagging_rules`, getDefaultOptions());
  const data = await handleResponse<PendingRulesListResponse>(response);
  return data.pendingRules;
}

/**
 * Approve a pending tagging rule
 */
export async function approvePendingRule(
  id: number,
  options?: {
    vendorOverride?: string;
    patternOverride?: string;
  }
): Promise<ApprovePendingRuleResponse> {
  const body: ApprovePendingRuleRequest = {
    vendorOverride: options?.vendorOverride ?? null,
    patternOverride: options?.patternOverride ?? null,
  };

  const response = await fetch(`${API_BASE}/pending_tagging_rules/${id}/approve`, {
    ...getDefaultOptions(),
    method: 'POST',
    body: JSON.stringify(body),
  });

  return handleResponse<ApprovePendingRuleResponse>(response);
}

/**
 * Reject a pending tagging rule
 */
export async function rejectPendingRule(id: number): Promise<RejectPendingRuleResponse> {
  const response = await fetch(`${API_BASE}/pending_tagging_rules/${id}/reject`, {
    ...getDefaultOptions(),
    method: 'POST',
  });

  return handleResponse<RejectPendingRuleResponse>(response);
}

// =============================================================================
// Reports API
// =============================================================================

/**
 * Get vendor spending report
 */
export async function getVendorSpendingReport(options?: {
  startDate?: string;
  endDate?: string;
}): Promise<VendorSpendingReport> {
  const params = new URLSearchParams();
  if (options?.startDate) params.set('start_date', options.startDate);
  if (options?.endDate) params.set('end_date', options.endDate);

  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE}/reports/vendors?${queryString}`
    : `${API_BASE}/reports/vendors`;

  const response = await fetch(url, getDefaultOptions());
  return handleResponse<VendorSpendingReport>(response);
}

/**
 * Get recurring expenses report
 */
export async function getRecurringExpensesReport(): Promise<RecurringExpensesReport> {
  const response = await fetch(`${API_BASE}/reports/recurring`, getDefaultOptions());
  return handleResponse<RecurringExpensesReport>(response);
}

/**
 * Get project costs report
 */
export async function getProjectCostsReport(): Promise<ProjectCostsReport> {
  const response = await fetch(`${API_BASE}/reports/projects`, getDefaultOptions());
  return handleResponse<ProjectCostsReport>(response);
}

/**
 * Get tax deductions report
 */
export async function getTaxDeductionsReport(): Promise<TaxDeductionsReport> {
  const response = await fetch(`${API_BASE}/reports/tax_deductions`, getDefaultOptions());
  return handleResponse<TaxDeductionsReport>(response);
}
